/**
 * Utility functions to clean up legacy localStorage data that doesn't follow
 * organization-specific patterns.
 */

/**
 * List of legacy localStorage keys that should be removed.
 * These keys were used before implementing organization-specific storage.
 */
const LEGACY_KEYS = [
  'fitness_app_calendar_filters', // Legacy calendar filters (without org ID)
  'fitness_app_clients_list_state', // Legacy client list state (without org ID)
  'fitness_app_clients_scroll', // Legacy client scroll position (without org ID)
  // Add other legacy keys here as needed
];

/**
 * Clean up legacy localStorage data that doesn't follow organization-specific patterns.
 * This should be called during app initialization to remove any old data that might
 * cause conflicts when switching between organizations.
 */
export const cleanupLegacyLocalStorage = (): void => {
  console.log('Cleaning up legacy localStorage data...');
  
  let removedCount = 0;
  
  LEGACY_KEYS.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      removedCount++;
      console.log(`Removed legacy localStorage key: ${key}`);
    }
  });
  
  if (removedCount > 0) {
    console.log(`Cleaned up ${removedCount} legacy localStorage keys`);
  } else {
    console.log('No legacy localStorage data found to clean up');
  }
};

/**
 * Get all localStorage keys that match a specific pattern.
 * Useful for debugging or advanced cleanup operations.
 */
export const getLocalStorageKeysByPattern = (pattern: RegExp): string[] => {
  const keys: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && pattern.test(key)) {
      keys.push(key);
    }
  }
  
  return keys;
};

/**
 * Remove all localStorage keys that match a specific pattern.
 * Use with caution - this is a destructive operation.
 */
export const removeLocalStorageKeysByPattern = (pattern: RegExp): number => {
  const keysToRemove = getLocalStorageKeysByPattern(pattern);
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`Removed localStorage key: ${key}`);
  });
  
  return keysToRemove.length;
};

/**
 * Debug function to log all current localStorage keys.
 * Useful for development and debugging.
 */
export const debugLocalStorageKeys = (): void => {
  console.log('Current localStorage keys:');
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const value = localStorage.getItem(key);
      console.log(`  ${key}:`, value?.substring(0, 100) + (value && value.length > 100 ? '...' : ''));
    }
  }
};
